import { ApiService } from "./api.service";
import {
  CreateTaskModel,
  TaskModel,
  TaskOptions,
  TaskViewModel,
  TaskSettingsModel,
  ReportsModel,
  CreateVisitTaskModel,
  VisitHistoryModel,
  ViewVisitTaskModel,
  TaskOptionModel,
} from "../models/tasks/tasks.model";
import { IResponse } from "../models/response";
import { useQuery, useMutation, MutationFunction } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { saveAs } from "file-saver";
import { showErrorAlert } from "../components/shared/alerts";
import { token, BASE_URL } from "./index";
import { AssigneesUsers, ReportsToUsers } from "../models/users/users-by-roles.model";
import { InfoModel } from "../models/info.model";

/**
 * Hook to fetch a list of tasks related to a building.
 *
 * @param {string} [buildingId] - Building ID.
 * @param {number} [page] - Page number.
 * @param {number} [itemsPerPage=10] - Items per page.
 * @param {Record<string, unknown>} [filters] - Optional filters.
 * @param {Record<string, unknown>} [sort] - Optional sorting criteria.
 * @returns {Object} React Query object containing tasks and a `refetch` function.
 */
export const useFetchTasks = (
  query: string = "",  //add query to search
  buildingId?: string,
  page?: number,
  itemsPerPage: number = 10,
  filters?: Record<string, unknown>,
  sort?: Record<string, unknown>
) => {
  const queryKey = [
    "tasks",
    query,
    buildingId,
    page,
    itemsPerPage,
    JSON.stringify(filters),
    sort,
  ];
  const { refetch, ...queryResult } = useQuery<
    IResponse<TaskModel[]>,
    AxiosError
  >(queryKey, async () => {
    const params: Record<string, unknown> = {
      query: query || "",
      page: page,
      per_page: itemsPerPage,
      sortField: sort?.field,
      sortDirection: sort?.sort,
      ...filters,
    };
    const tasksData = await ApiService.get(`/buildings/${buildingId}/tasks`, {
      params,
    });
    return tasksData?.data;
  });
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch a list of assigned tasks.
 *
 * @param {number} [page=1] - Page number.
 * @param {number} [itemsPerPage=10] - Items per page.
 * @param {Record<string, unknown>} [filters] - Optional filters.
 * @param {Record<string, unknown>} [sort] - Optional sorting criteria.
 * @returns {Object} React Query object containing assigned tasks and a `refetch` function.
 */
export const useFetchAssignedTasks = (
  query: string = "",  //add query to search
  page = 1,
  itemsPerPage = 10,
  filters?: Record<string, unknown>,
  sort?: Record<string, unknown>
) => {
  const queryKey = [
    "assigned-tasks",
    query,
    page,
    itemsPerPage,
    JSON.stringify(filters),
    sort,
  ];
  const { refetch, ...queryResult } = useQuery<
    IResponse<TaskModel[]>,
    AxiosError
  >(queryKey, async () => {
    const params = {
      query: query || "",
      page,
      per_page: itemsPerPage,
      sortField: sort?.field,
      sortDirection: sort?.sort,
      ...filters,
    };
    const assignedTasksData = await ApiService.get(`/activities`, { params });
    return assignedTasksData.data;
  });
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch a list of drinking water tasks.
 * Fetches tasks filtered by clients with customer type "Trinkwasser" (schicht).
 *
 * @param {number} [page=1] - Page number.
 * @param {number} [itemsPerPage=10] - Items per page.
 * @param {Record<string, unknown>} [filters] - Optional filters.
 * @param {Record<string, unknown>} [sort] - Optional sorting criteria.
 * @returns {Object} React Query object containing drinking water tasks and a `refetch` function.
 */
export const useFetchDrinkingWaterTasks = (
  query: string = "",  //add query to search
  page = 1,
  itemsPerPage = 10,
  filters?: Record<string, unknown>,
  sort?: Record<string, unknown>
) => {
  const queryKey = [
    "drinking-water-tasks",
    query,
    page,
    itemsPerPage,
    JSON.stringify(filters),
    sort,
  ];
  const { refetch, ...queryResult } = useQuery<
    IResponse<TaskModel[]>,
    AxiosError
  >(queryKey, async () => {
    const params = {
      query: query || "",
      page,
      per_page: itemsPerPage,
      sortField: sort?.field,
      sortDirection: sort?.sort,
      ...filters,
    };
    const drinkingWaterTasksData = await ApiService.get(`/drinking-water-tasks`, { params });
    return drinkingWaterTasksData.data;
  });
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch a list of resolved tasks for a building.
 *
 * @param {string} [buildingId] - Building ID.
 * @param {number} [page=1] - Page number.
 * @param {number} [itemsPerPage=12] - Items per page.
 * @param {Record<string, unknown>} [filters] - Optional filters.
 * @param {Record<string, unknown>} [sort] - Optional sorting criteria.
 * @returns {Object} React Query object with resolved tasks and a `refetch` function.
 */
export const useFetchResolvedTasks = (
  buildingId?: string,
  page = 1,
  itemsPerPage = 12,
  filters?: Record<string, unknown>,
  sort?: Record<string, unknown>
) => {
  const queryKey = [
    "resolved-tasks",
    buildingId,
    page,
    itemsPerPage,
    JSON.stringify(filters),
    sort,
  ];
  const { refetch, ...queryResult } = useQuery<
    IResponse<TaskModel[]>,
    AxiosError
  >(queryKey, async () => {
    const params = {
      page,
      per_page: itemsPerPage,
      sortField: sort?.field,
      sortDirection: sort?.sort,
      ...filters,
    };
    const resolvedTasksData = await ApiService.get(
      `/buildings/${buildingId}/resolved-tasks`,
      { params }
    );
    return resolvedTasksData.data;
  });
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch available task options.
 *
 * @returns {Object} React Query object with task options and a `refetch` function.
 */
export const useFetchTaskOptions = (enabled: boolean = true) => {
  const queryKey = ["task-options"];
  const { refetch, ...queryResult } = useQuery<
    IResponse<TaskOptions>,
    AxiosError
  >(queryKey, async () => {
    const tasksData = await ApiService.get(`/task-options`);
    return tasksData;
  },
    {
      enabled: enabled,
    }
  );
  return { ...queryResult, refetch };
};

/**
 * Hook to create a new task for a building.
 *
 * @param {string} [buildingId] - Building ID where the task will be created.
 * @returns {Object} Mutation object for creating a task.
 */
export const useCreateTask = (buildingId?: string) => {
  const createTask: MutationFunction<
    IResponse<CreateTaskModel>,
    FormData
  > = async (formData: FormData) => {
    const response = await ApiService.postFormData(
      `/buildings/${buildingId}/tasks`,
      formData
    );
    return response.data;
  };
  return useMutation(createTask);
};

/**
 * Hook to fetch details of a specific task.
 *
 * @param {string} [taskId] - Task ID.
 * @returns {Object} React Query object with task details and a `refetch` function.
 */
export const useGetTaskDetails = (taskId?: string) => {
  const queryKey = ["task", taskId];
  const { refetch, ...queryResult } = useQuery<
    IResponse<TaskViewModel>,
    AxiosError
  >(queryKey, async () => {
    const taskData = await ApiService.get(`/tasks/${taskId}`);
    return taskData;
  });

  return { ...queryResult, refetch };
};

/**
 * Hook to fetch available template types for tasks.
 *
 * @returns {Object} React Query object with template types and a `refetch` function.
 */
export const useFetchTemplateTypes = (enabled: boolean = true) => {
  const queryKey = ["template-types"];
  const { refetch, ...queryResult } = useQuery<
    IResponse<TaskOptionModel[]>,
    AxiosError
  >(queryKey, async () => {
    const typesData = await ApiService.get(`/tasks-templates`);
    return typesData;
  },
    {
      enabled: enabled
    });
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch settings for tasks under a building.
 *
 * @param {string} [buildingId] - Building ID.
 * @param {number} [page=1] - Page number.
 * @param {number} [itemsPerPage=12] - Items per page.
 * @param {Record<string, unknown>} [filters] - Optional filters.
 * @param {Record<string, unknown>} [sort] - Optional sorting criteria.
 * @returns {Object} React Query object with task settings and a `refetch` function.
 */
export const useFetchTaskSettings = (
  buildingId?: string,
  page = 1,
  itemsPerPage = 12,
  filters?: Record<string, unknown>,
  sort?: Record<string, unknown>
) => {
  const queryKey = [
    "setting-tasks",
    buildingId,
    page,
    itemsPerPage,
    JSON.stringify(filters),
    sort,
  ];
  const { refetch, ...queryResult } = useQuery<
    IResponse<TaskSettingsModel[]>,
    AxiosError
  >(queryKey, async () => {
    const params = {
      page,
      per_page: itemsPerPage,
      sortField: sort?.field,
      sortDirection: sort?.sort,
      ...filters,
    };
    const tasksData = await ApiService.get(
      `/buildings/${buildingId}/setting-tasks`,
      { params }
    );
    return tasksData?.data;
  });
  return { ...queryResult, refetch };
};

/**
 * Function to download a Mikro Excel file for a task.
 *
 * @param {string} taskId - ID of the task.
 * @param {"normal" | "follow-up"} type - Type of the task excel export.
 * @param {number} page - Page number to download.
 * @returns {Promise<void>}
 */
export const downloadAndSaveMikroExcelFile = async (
  taskId: string,
  type: "normal" | "follow-up",
  page: number
) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/tasks/${taskId}/excel/${type}?page=${page}`,
      {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type":
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
      }
    );

    // Get the file name from the header
    const contentDisposition = response.headers['content-disposition'];
    const filename = contentDisposition?.split('filename=')[1].replace(/"/g, '');

    if (response.status === 200) {
      saveAs(response.data, filename);
    } else {
      console.error(`Failed to fetch the Excel file for page ${page}`);
    }
  } catch (error: AxiosError | any) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        const { status, data } = error.response;
        let errorMessage = "Beim Herunterladen der Datei ist ein Fehler aufgetreten.";

        // Handle specific error codes
        switch (status) {
          case 400:
            errorMessage = data.message || "Ungültige Anfrage. Bitte überprüfen Sie Ihre Eingabe.";
            break;
          case 401:
            errorMessage = "Nicht autorisiert. Bitte melden Sie sich erneut an.";
            break;
          case 403:
            errorMessage = "Sie haben keine Berechtigung, auf diese Datei zuzugreifen.";
            break;
          case 404:
            errorMessage = "Die angeforderte Datei wurde nicht gefunden.";
            break;
          case 500:
            errorMessage = "Serverfehler. Bitte versuchen Sie es später erneut.";
            break;
          default:
            errorMessage = data?.message || "Ein unerwarteter Fehler ist aufgetreten.";
        }

        console.error(`Error ${status}: ${errorMessage}`);
        showErrorAlert(errorMessage);
      } else {
        // Any other error
        console.error("Fehler:", error.message);
        showErrorAlert("Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
      }
    }
  }
};

/**
 * Hook to fetch report details for a task.
 *
 * @param {string} [taskId] - Task ID.
 * @returns {Object} React Query object with report details and a `refetch` function.
 */
export const useGetTaskReportsDetails = (taskId?: string) => {
  const queryKey = ["reports", taskId];
  const { refetch, ...queryResult } = useQuery<
    IResponse<ReportsModel>,
    AxiosError
  >(queryKey, async () => {
    const taskData = await ApiService.get(`/tasks/${taskId}/reports`);
    return taskData;
  });

  return { ...queryResult, refetch };
};

/**
 * Hook to update/finish a task.
 *
 * @param {string} [taskId] - Task ID.
 * @returns {Object} Mutation object to update or end a task.
 */
export const useUpdateTask = (taskId?: string) => {
  const updateTask: MutationFunction<IResponse<TaskViewModel>, FormData> = async (
    formData: FormData
  ) => {
    const response = await ApiService.postFormData(
      `/tasks/${taskId}?_method=PATCH`,
      formData
    );
    return response;
  };
  return useMutation(updateTask);
};

/**
 * Helper function for authenticated file downloads with proper error handling
 *
 * @param {string} url - The API endpoint URL, or a raw storage path served by /api/files/download
 * @param {Record<string, any>} [params] - Optional query parameters
 * @param {string} [defaultFilename] - Saved file name; if omitted, uses Content-Disposition when present
 * @returns {Promise<void>}
 */
export const downloadAuthenticatedFile = async (
  url: string,
  params?: Record<string, any>,
  defaultFilename?: string
): Promise<void> => {
  try {
    const response = await axios.get(url, {
      params,
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    saveDownloadedFile(response, defaultFilename);
  } catch (error: AxiosError | any) {
    handleFileDownloadError(error);
  }
};

/**
 * Helper function for authenticated file downloads that need to send a request body.
 *
 * Same behaviour as downloadAuthenticatedFile, but sends the payload as a POST body
 * instead of query parameters, for payloads that are too large or too nested for a
 * query string (e.g. the selected parts of a plant report).
 *
 * @param {string} url - The API endpoint URL
 * @param {Record<string, any>} [body] - The request body
 * @param {string} [defaultFilename] - Saved file name; if omitted, uses Content-Disposition when present
 * @returns {Promise<void>}
 */
export const downloadAuthenticatedFilePost = async (
  url: string,
  body?: Record<string, any>,
  defaultFilename?: string
): Promise<void> => {
  try {
    const response = await axios.post(url, body, {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    saveDownloadedFile(response, defaultFilename);
  } catch (error: AxiosError | any) {
    handleFileDownloadError(error);
  }
};

/**
 * Save a downloaded file response, naming it after the Content-Disposition header
 * when the server sent one.
 *
 * @param {any} response - The axios response holding the file blob
 * @param {string} [defaultFilename] - Name to fall back to
 */
const saveDownloadedFile = (response: any, defaultFilename?: string): void => {
  // Get the file name from the header or use the default
  const contentDisposition = response.headers['content-disposition'];
  const filename = contentDisposition?.split('filename=')[1]?.replace(/"/g, '') || defaultFilename || 'download';

  if (response.status === 200) {
    saveAs(response.data, filename);
  } else {
    console.error('Failed to download the file');
    showErrorAlert('Failed to download the file');
  }
};

/**
 * Show a German error message for a failed file download.
 *
 * @param {AxiosError | any} error - The error thrown by the request
 */
const handleFileDownloadError = (error: AxiosError | any): void => {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      const { status, data } = error.response;
      let errorMessage = "Beim Herunterladen der Datei ist ein Fehler aufgetreten.";

      // Handle specific error codes
      switch (status) {
        case 400:
          errorMessage = (data as any)?.message || "Ungültige Anfrage. Bitte überprüfen Sie Ihre Eingabe.";
          break;
        case 401:
          errorMessage = "Nicht autorisiert. Bitte melden Sie sich erneut an.";
          break;
        case 403:
          errorMessage = "Sie haben keine Berechtigung, auf diese Datei zuzugreifen.";
          break;
        case 404:
          errorMessage = "Die angeforderte Datei wurde nicht gefunden.";
          break;
        case 500:
          errorMessage = "Serverfehler. Bitte versuchen Sie es später erneut.";
          break;
        default:
          errorMessage = (data as any)?.message || "Ein unerwarteter Fehler ist aufgetreten.";
      }

      console.error(`Error ${status}: ${errorMessage}`);
      showErrorAlert(errorMessage);
    } else {
      // Any other error
      console.error("Fehler:", error.message);
      showErrorAlert("Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
    }
  }
};

/**
 * Function to generate the annual report for a plant.
 *
 * @param {string} [plantId] - Plant ID.
 * @param {Record<string, unknown>} [filters] - Optional filters to apply.
 * @returns {Promise<boolean>} True if successful, false otherwise.
 */
export const FetchReport = async (
  plantId?: string,
  filters?: Record<string, unknown>
): Promise<boolean> => {
  const url = `/plants/${plantId}/annual-report`;

  try {
    await ApiService.get(url, { params: filters });
    return true; // Return true if the API call is successful
  } catch (error) {
    console.error("Error fetching report:", error); // Log the error for debugging
    return false; // Return false if an error occurs
  }
};

/**
 * Hook to create a client visit task for a client.
 *
 * @param {string} [clientId] - Client ID.
 * @returns {Object} Mutation object to create a visit task.
 */
export const useCreateVisitTask = (clientId?: string) => {
  const createVisitTask: MutationFunction<
    IResponse<CreateVisitTaskModel>,
    FormData
  > = async (formData: FormData) => {
    const response = await ApiService.postFormData(
      `/clients/${clientId}/visits`,
      formData
    );
    return response.data;
  };
  return useMutation(createVisitTask);
};

/**
 * Hook to create an Order Handling IW task for a client.
 *
 * @param {string} [clientId] - Client ID.
 * @returns {Object} Mutation object to create an Order Handling IW task.
 */
export const useCreateOrderHandlingIWTask = (clientId?: string) => {
  const createOrderHandlingIWTask: MutationFunction<
    IResponse<TaskViewModel>,
    void
  > = async () => {
    const response = await ApiService.post(
      `/clients/${clientId}/order-handling-iw`
    );
    return response;
  };
  return useMutation(createOrderHandlingIWTask);
};

/**
 * Hook to fetch history of visit tasks for a client.
 *
 * @param {string} [clientId] - Client ID.
 * @param {number} [page=1] - Page number.
 * @param {number} [itemsPerPage=12] - Items per page.
 * @param {Record<string, unknown>} [filters] - Optional filters.
 * @param {Record<string, unknown>} [sort] - Optional sorting options.
 * @returns {Object} React Query object with visit history.
 */
export const useFetchVisitsHistory = (
  clientId?: string,
  page = 1,
  itemsPerPage = 12,
  filters?: Record<string, unknown>,
  sort?: Record<string, unknown>,
) => {
  const queryKey = [
    "task-history",
    clientId,
    page,
    itemsPerPage,
    JSON.stringify(filters),
    sort,
  ];
  const queryResult = useQuery<IResponse<VisitHistoryModel[]>, AxiosError>(
    queryKey,
    async () => {
      const params = {
        page,
        per_page: itemsPerPage,
        sortField: sort?.field,
        sortDirection: sort?.sort,
        ...filters,
      };
      const visitTasksData = await ApiService.get(
        `/clients/${clientId}/task-history`,
        { params }
      );
      return visitTasksData.data;
    }
  );
  return queryResult;
};

/**
 * Hook to fetch details of a visit task.
 *
 * @param {string} [id] - Visit ID.
 * @returns {Object} React Query object with visit task details.
 */
export const useGetVisitDetails = (id?: string) => {
  const queryKey = ["visits", id];

  const queryResult = useQuery<IResponse<ViewVisitTaskModel>, AxiosError>(
    queryKey,
    () => ApiService.get(`/visits/${id}`),
    {
      enabled: Boolean(id),
    }
  );

  return queryResult;
};

/**
 * Hook to fetch list of assignees for a task.
 *
 * @returns {Object} React Query object with assignees list.
 */
export const useFetchAssigneesUsers = () => {
  const queryKey = ["assignees-users"];
  const { refetch, ...queryResult } = useQuery<
    IResponse<AssigneesUsers[]>,
    AxiosError
  >(queryKey, async () => {
    const typesData = await ApiService.get(`/get-task-assignees`);
    return typesData;
  });
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch "reports to" options for a specific task.
 *
 * @param {string} [taskId] - Task ID.
 * @param {boolean} [enabled=true] - Whether to enable fetching.
 * @returns {Object} React Query object with "reports to" users options.
 */
export const useFetchTaskReportsToOptions = (taskId?: string, enabled: boolean = true) => {
  const queryKey = ["reports-to-options", taskId];
  const { refetch, ...queryResult } = useQuery<
    IResponse<ReportsToUsers[]>,
    AxiosError
  >(queryKey, async () => {
    const reportsToUsers = await ApiService.get(`/tasks/${taskId}/get-task-reports-to-options`);
    return reportsToUsers;
  },
    {
      enabled: !!taskId && enabled, // Ensures the query only runs if `taskId` exists and `enabled` is true
    });

  return { ...queryResult, refetch };
};

/**
 * Hook to fetch reports to users based on their assigned role names with the logged-in user.
 *
 * @param {string[]} roleNames - List of role names to filter users by.
 * @returns {Object} A React Query object containing users grouped by roles and a `refetch` function.
 */
export const useFetchReportsToOptions = (roleNames: string[]) => {
  const queryKey = ["reportsToOptions", JSON.stringify(roleNames)];
  const { refetch, ...queryResult } = useQuery<
    IResponse<InfoModel[]>,
    AxiosError
  >(
    queryKey,
    async () => {
      const roleNamesParam = roleNames.join("&roleNames[]=");
      const reportsToOptions = await ApiService.get(
        `/get-reports-to-options?roleNames[]=${roleNamesParam}`
      );
      return reportsToOptions;
    },
    { staleTime: 0 }
  );
  return { ...queryResult, refetch };
};